import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import DatePicker, { MonthPicker } from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, ExportButtons, FilterSelect, ReportTable, useMeta } from "@/components/common";
import EmployeePicker from "@/components/EmployeePicker";
import { api, errMsg, download } from "@/lib/api";
import { currentMonth, TXN } from "@/lib/format";
import { toast } from "sonner";

const TABS = [
  ["outstanding", "Employee-wise Outstanding"],
  ["deductions", "Monthly Deductions"],
  ["ledger", "Complete Ledger"],
  ["summary", "Outstanding Summary"],
];

function Filters({ tab, f, setF, meta }) {
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 no-print" data-testid="report-filters">
      {tab === "deductions" && (
        <div><Label className="text-xs">Month</Label><div className="mt-1"><MonthPicker value={f.month} onChange={set("month")} testid="report-month" /></div></div>
      )}
      {tab !== "summary" && !(tab === "deductions" && f.month) && (
        <div><Label className="text-xs">From date</Label><DatePicker className="mt-1 h-10" value={f.date_from} onChange={set("date_from")} clearable testid="report-date-from" /></div>
      )}
      {!(tab === "deductions" && f.month) && (
        <div><Label className="text-xs">{tab === "summary" ? "As of date" : "To date"}</Label><DatePicker className="mt-1 h-10" value={f.date_to} onChange={set("date_to")} clearable testid="report-date-to" /></div>
      )}
      <div><Label className="text-xs">Department</Label><div className="mt-1"><FilterSelect value={f.department} onChange={set("department")} options={meta.departments} placeholder="All departments" testid="report-dept" className="w-full" /></div></div>
      <div><Label className="text-xs">Branch</Label><div className="mt-1"><FilterSelect value={f.branch} onChange={set("branch")} options={meta.branches} placeholder="All branches" testid="report-branch" className="w-full" /></div></div>
      {tab !== "summary" && (
        <div className="sm:col-span-2">
          <Label className="text-xs">Employee</Label>
          <div className="mt-1 flex gap-2">
            <div className="flex-1"><EmployeePicker value={f.employee} onChange={set("employee")} testid="report-employee-picker" placeholder="All employees" /></div>
            {f.employee && <Button variant="ghost" size="icon" className="h-12" onClick={() => set("employee")(null)} data-testid="report-employee-clear"><X className="h-4 w-4" /></Button>}
          </div>
        </div>
      )}
      {tab === "ledger" && (
        <div><Label className="text-xs">Transaction type</Label><div className="mt-1"><FilterSelect value={f.type} onChange={set("type")} options={Object.entries(TXN).map(([value, t]) => ({ value, label: t.label }))} placeholder="All types" testid="report-type" className="w-full" /></div></div>
      )}
      {tab === "outstanding" && (
        <label className="flex items-center gap-2 text-sm self-end pb-2"><Switch checked={f.only_outstanding} onCheckedChange={set("only_outstanding")} data-testid="report-only-outstanding" /> Only employees with outstanding</label>
      )}
      {tab === "summary" && (
        <div><Label className="text-xs">Group by</Label><div className="mt-1"><FilterSelect value={f.group_by} onChange={(v) => set("group_by")(v || "department")} options={[{ value: "department", label: "Department" }, { value: "branch", label: "Branch" }]} placeholder="Department" testid="report-group-by" className="w-full" /></div></div>
      )}
      {tab === "deductions" && f.month && (
        <button className="text-xs text-blue-800 self-end pb-3 text-left hover:underline" onClick={() => set("month")("")} data-testid="report-use-range">Use a date range instead</button>
      )}
    </div>
  );
}

export default function Reports() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "outstanding";
  const meta = useMeta();
  const [f, setF] = useState({ month: currentMonth(), date_from: "", date_to: "", department: "", branch: "", employee: null, type: "", only_outstanding: false, group_by: "department" });
  const [data, setData] = useState(null);

  const params = () => {
    const p = { date_from: f.date_from, date_to: f.date_to, department: f.department, branch: f.branch, code: f.employee?.code || "" };
    if (tab === "deductions") { p.month = f.month; if (f.month) { p.date_from = ""; p.date_to = ""; } }
    if (tab === "ledger") p.type = f.type;
    if (tab === "outstanding") { p.only_outstanding = f.only_outstanding; p.status = ""; }
    if (tab === "summary") { p.group_by = f.group_by; delete p.code; delete p.date_from; }
    return p;
  };

  useEffect(() => {
    setData(null);
    const t = setTimeout(() => {
      api.get(`/reports/${tab}`, { params: params() }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, f]);

  return (
    <div>
      <PageHeader title="Reports" subtitle="Filter, review and export advance reports" actions={<ExportButtons prefix="report" onExport={(fmt) => download(`/reports/${tab}`, { ...params(), format: fmt })} />} />
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v })} className="mb-4">
        <TabsList className="flex-wrap h-auto">
          {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`report-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <Filters tab={tab} f={f} setF={setF} meta={meta} />
      <div className="mt-4">
        {data && <div className="mb-2"><div className="font-heading font-semibold text-slate-800" data-testid="report-title">{data.title}</div><div className="text-xs text-slate-500">{data.subtitle}</div></div>}
        {data ? <ReportTable data={data} testid="report-table" /> : <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>}
      </div>
    </div>
  );
}
